import { toast } from 'react-toastify';
import { uuidv7 } from 'uuidv7';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebaseApp';
import { storage } from '../config/firebaseApp';
import { ref, uploadBytes } from 'firebase/storage';
import {
  doc,
  getDocs,
  getDoc,
  collection,
  query,
  where,
  Timestamp,
  onSnapshot,
  orderBy,
  startAfter,
  limit,
  QueryDocumentSnapshot,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../config/firebaseApp';
import { ClientStatus } from '../utils/clientStatus';
import { ClientMapStatus } from '../utils/clientMapStatus';
import { getMultipleUserInfoByUserIds, getUserFullNameById } from './user.service';
import { getClientMapDataByMultipleClientId } from './programTrackClientMap.service';
import { fetchClientReportedIpv4 } from '../utils/clientIpv4';
import { getMultipleProgramTracksById } from './programTrack.service';
import { v4 as uuidv4 } from 'uuid';
import { shouldShowButton } from '../pages/admin/dashboard/dashboardAccessControl';
import { UserRolesEnum } from '../utils/rolesEnum';
import { UserModel } from '../store/user/userModel';
import { convertTimeStampToDate } from '../utils/dateTime';
import { ClientAccount } from '../utils/clientAccount';
import { userAccountStatus } from '../utils/userUtils';
import { IClientJRTComplete } from '../store/employmentModule/workshopModal';
import { ISortModel } from '../utils/common';
import { getAuth } from 'firebase/auth';

import {
  activeStatusOptions,
  educationOptions,
  empStatusOptions,
  employmentTypes,
  ethnicityOptions,
  genderOptions,
  highestDLOptions,
  interestedInFilterOptions,
  publicAssistanceOptions,
  referralSourceOptions,
  statusOptions,
} from '../utils/filterOptions';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getMultipleJobByIdsForReport } from './employmentModule.service';
import { jobOfferStatusEnums } from '../components/ClientEmployment/EmploymentEnum';
import { caseManagerTypes, getReferralSourceByCaseManagerType } from '../utils/caseManagerTypes';
import { IMonitoringEntry } from '../store/monitoringSystem/monitoringSystemModal';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IInfoSession {
  createdBy: string;
  description: string;
  location: string;
  title: string;
  dateTime: number;
}

export interface IUser {
  id?: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  createdBy?: string;
  createdAt?: any;
}

export interface IProspectiveClientModel extends IUser {
  phoneNumber?: string;
  infoSessionId?: string;
  programTrack?: string;
  infoSessionData?: IInfoSession;
  interestedIn?: any[];
  status?: string;
}

export interface ClientModel extends IUser {
  address1: string;
  address2: string;
  mobile: string;
  state: string;
  homeNumber: string;
  city: string;
  zip: string;
  workNumber: string;
  dob: string;
  ssn: string;
  gender: string;
  otherGender: string;
  ethnic: string;
  employmentStatus: string;
  educationalBackground: string;
  urls: Array<string>;
  notes: INote[] | Array<object>;
  status?: string;
  id?: string;
  assignedTo?: string;
  phoneNumber?: string;
  isInsured: string;
  caseNumber: string;
  publicAssistance: string[];
  publicAssistanceOther: string;
  referralSource: string;
  refCommunityOrg: string;
  refSourceEntryDate?: Timestamp;
  interestedIn?: any[];
  deleteCaseNumber: string;
  ethnicOther: string;
  highestDL: string;
  isUserAgreeToTerms?: boolean;
  isAgreeToTextMessaging?: boolean;
  isUserAgreeToPrivacyPolicy?: boolean;
  scheduledJobInterview?: any[];
}

export interface ICommonClient extends ClientModel, IProspectiveClientModel {}

export interface ICLientMetaInfo {
  totalRecords: number;
  currentPage: number;
  clients: Array<any>;
  totalPages: number;
}

export interface IResponse {
  success: boolean;
  message: string;
}

export interface IData {
  id: string;
  fourDigitSSN: string;
  dob: Date;
}
export interface IDocumentData {
  name: string;
  file: Object;
  uploadedBy: string;
  role: string;
}

export interface INote {
  category?: string;
  title: string;
  description: string;
  createdBy: string;
  role: string;
  clientId?: string;
  createdAt?: Timestamp;
  noteId?: string;
}

export interface IEditNote {
  category?: string;
  title: string;
  description: string;
  clientId: string;
  noteId: string;
}

export interface ICancelIndividualEnrollment {
  id: string;
  programTrackId: string;
}

export interface IResendInvitationMail {
  firstName: string;
  email: string;
  infoSessionIds: string[];
}

export interface ISendTextMessage {
  ids: string[];
  text: string;
}

export interface IClientEmplInfo {
  id: string;
  employmentModuleStatus: string;
  employmentType: string;
}

const mapIdsToValues = (ids: string[], options: { id: string; value: string }[]) => {
  return ids.map((id) => options.find((option) => option.id === id)?.value || '');
};

export const updateClientInfo = async (client: ClientModel, clientId: string) => {
  try {
    const payload = { ...client, id: clientId };
    const response: any = await httpsCallable(functions, 'editClientInfoV2')(payload);
    if (response.data.success) {
      toast.success('Client Info Updated Successfully!');
    }
  } catch (error: any) {
    toast.error('Error Updating Client Info!');
  }
};

export const addNewClient = async (client: ClientModel, isSelfCreated: boolean, user?: UserModel) => {
  let clientInfo = { ...client };
  if (!isSelfCreated && user) {
    if (client.notes.length > 0) {
      client.notes[0] = { ...client.notes[0], createdBy: user?.id || '', role: user?.role || '', noteId: uuidv4() };
      clientInfo.notes = client.notes;
    } else {
      clientInfo.notes = [];
    }
  }

  const clientReportedIpv4 = await fetchClientReportedIpv4();
  const response: any = await httpsCallable(functions, 'createNewClientV2')({ ...clientInfo, clientReportedIpv4 });

  return response.data;
};

export const convertToClient = async (data: IData) => {
  const clientReportedIpv4 = await fetchClientReportedIpv4();
  const response = await httpsCallable(functions, 'convertProspectiveClientV2')({ ...data, clientReportedIpv4 });
  return response.data as Promise<IResponse>;
};

export const resendInvitationMailToPC = async (data: IResendInvitationMail, userRole: string) => {
  const hasPermission = shouldShowButton('resendInvitationMailToPC', userRole);
  if (!hasPermission) {
    throw new Error('You do not have permission to resend mail to Prospective Client');
  }
  const clientReportedIpv4 = await fetchClientReportedIpv4();
  const response = await httpsCallable(functions, 'resendInvitationEmailToPCV2')({ ...data, clientReportedIpv4 });

  return response.data as Promise<IResponse>;
};

export const listClients = async (page: number, clientStatuses?: Array<String>, is154ECompleted?: boolean) => {
  let params: any = {
    page,
  };
  if (clientStatuses !== undefined) params['clientStatuses'] = clientStatuses;
  if (is154ECompleted !== undefined) params['is154ECompleted'] = is154ECompleted;
  const response: any = await httpsCallable(functions, 'listClientsV2')(params);

  return response.data as Promise<IResponse & { data: ICLientMetaInfo }>;
};

export const getProgramTrackName = (programTrackData: any[]): string => {
  let ptName = '';

  ptName =
    programTrackData.find(
      (ptData) => ptData.status === ClientMapStatus.ACTIVE || ptData.status === ClientMapStatus.PRE_COMPLETE
    )?.programTrack || '';

  if (!ptName) {
    if (programTrackData.some((ptData) => ptData.status === ClientMapStatus.REVIEW)) {
      const ptNameArr: string[] = [];
      for (const ptData of programTrackData) {
        if (ptData.status === ClientMapStatus.REVIEW) {
          ptNameArr.push(ptData.programTrack);
        }
      }
      ptName = ptNameArr.join(' ');
    }
  }

  if (!ptName) {
    ptName = programTrackData.find((ptData) => ptData.status === ClientMapStatus.COMPLETE)?.programTrack || '';
  }

  if (!ptName) {
    ptName = programTrackData.find((ptData) => ptData.status === ClientMapStatus.DROP_OUT)?.programTrack || '';
  }

  return ptName;
};

export const fetchClientList = async (user: UserModel, filterStatus?: string) => {
  try {
    const clientListRef = collection(db, 'clientList');
    const constraints: any[] = [];

    if (filterStatus && filterStatus !== 'all') {
      constraints.push(where('clientStatus', '==', filterStatus));
    }

    const clientListSnapshot = await getDocs(query(clientListRef, ...constraints));

    if (clientListSnapshot.empty) return [];

    const clientList = clientListSnapshot.docs.map((doc) => {
      const programTrackData = doc.data()?.programTrackData || [];
      const programTrackName = getProgramTrackName(programTrackData);

      return {
        id: doc.id,
        ...doc.data(),
        programTrackName,
      };
    });

    // Case Manager filtering
    if (user.role === UserRolesEnum.CASE_MANAGER) {
      if (user.caseManagerType === caseManagerTypes.PINCC_PARTNER) return [];

      const referralSource = getReferralSourceByCaseManagerType(user?.caseManagerType || '');
      if (referralSource) {
        const cmQuery = query(collection(db, 'clients'), where('referralSource', '==', referralSource));
        const cmSnapshot = await getDocs(cmQuery);
        if (cmSnapshot.empty) return [];

        const clientIds = new Set(cmSnapshot.docs.map((doc) => doc.id));
        return clientList.filter((client) => clientIds.has(client.id));
      }
    }

    // Trainer filtering
    if (user.role === UserRolesEnum.TRAINER) {
      const trackClientMapQuery = query(collection(db, 'programTrackClientMap'), where('trainerId', '==', user.id));
      const trackClientMapSnapshot = await getDocs(trackClientMapQuery);
      if (trackClientMapSnapshot.empty) return [];

      const clientIds = new Set(trackClientMapSnapshot.docs.map((doc) => doc.data().clientId));
      return clientList.filter((client) => clientIds.has(client.id));
    }

    return clientList;
  } catch (err) {
    toast.error('Error Retrieving Client Data!');
    return [];
  }
};

export const fetchClientListWithCount = async (
  user: UserModel,
  filterStatus: string,
  pageSize: number,
  lastVisibleDoc?: QueryDocumentSnapshot<any>
): Promise<{
  clients: any[];
  lastDoc: QueryDocumentSnapshot<any> | null;
  totalRecords: number;
}> => {
  try {
    const clientListRef = collection(db, 'clientList');
    const constraints: any[] = [];

    if (filterStatus && filterStatus !== 'all') {
      constraints.push(where('clientStatus', '==', filterStatus));
    }

    // Always sort by createdAt DESC
    constraints.push(orderBy('createdAt', 'desc'));

    // Count total records
    const countQuery = query(clientListRef, ...constraints.filter((c) => !(c instanceof limit)));
    const countSnap = await getCountFromServer(countQuery);
    const totalRecords = countSnap.data().count;

    // Pagination
    if (lastVisibleDoc) {
      constraints.push(startAfter(lastVisibleDoc));
    }
    constraints.push(limit(pageSize));

    const pagedQuery = query(clientListRef, ...constraints);
    const clientListSnapshot = await getDocs(pagedQuery);
    const clientDocs = clientListSnapshot.docs;

    let clientList = clientDocs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      programTrackName: getProgramTrackName(doc.data()?.programTrackData || []),
    }));

    // Role filtering
    if (user.role === UserRolesEnum.CASE_MANAGER) {
      if (user.caseManagerType === caseManagerTypes.PINCC_PARTNER)
        return { clients: [], lastDoc: null, totalRecords: 0 };
      const referralSource = getReferralSourceByCaseManagerType(user?.caseManagerType || '');
      if (referralSource) {
        const cmQuery = query(collection(db, 'clients'), where('referralSource', '==', referralSource));
        const cmSnapshot = await getDocs(cmQuery);
        const clientIds = new Set(cmSnapshot.docs.map((doc) => doc.id));
        clientList = clientList.filter((client) => clientIds.has(client.id));
      }
    }

    if (user.role === UserRolesEnum.TRAINER) {
      const mapQuery = query(collection(db, 'programTrackClientMap'), where('trainerId', '==', user.id));
      const mapSnapshot = await getDocs(mapQuery);
      const clientIds = new Set(mapSnapshot.docs.map((doc) => doc.data().clientId));
      clientList = clientList.filter((client) => clientIds.has(client.id));
    }

    return {
      clients: clientList,
      lastDoc: clientDocs[clientDocs.length - 1] || null,
      totalRecords,
    };
  } catch (err) {
    toast.error('Error Retrieving Client Data!');
    return { clients: [], lastDoc: null, totalRecords: 0 };
  }
};

let paginationCursors: QueryDocumentSnapshot[] = [];

export const listClientByQuery = async (
  page: number,
  filterStatus: string,
  sortModel: ISortModel[],
  user: UserModel
): Promise<ICLientMetaInfo | undefined> => {
  try {
    const pageSize = 10;
    const cursor = page > 1 ? paginationCursors[page - 2] : undefined;

    const { clients, lastDoc, totalRecords } = await fetchClientListWithCount(user, filterStatus, pageSize, cursor);

    if (clients.length === 0) {
      return {
        totalRecords: 0,
        currentPage: page,
        clients: [],
        totalPages: 0,
      };
    }

    if (page > paginationCursors.length && lastDoc) {
      paginationCursors.push(lastDoc);
    }

    const totalPages = Math.ceil(totalRecords / pageSize);

    return {
      totalRecords,
      currentPage: page,
      clients,
      totalPages,
    };
  } catch (err: any) {
    toast.error('Error Retrieving Client Data!');
  }
};

export const searchClientsByQuery = async (
  searchQuery: string,
  searchQueryType: string,
  user: UserModel,
  page: number = 1,
  pageSize: number = 10,
  filterStatus: string = 'all'
) => {
  try {
    if (!searchQuery) throw new Error('Missing search query');

    const currentUser = getAuth().currentUser;
    console.log('currentUser in searchClientsByQuery', currentUser);
    const response: any = await httpsCallable(
      functions,
      'searchClientByNameV2'
    )({
      query: searchQuery.includes('@') ? searchQuery.split('@')[0] : searchQuery,
      type: searchQueryType,
      page,
      pageSize,
      status: filterStatus,
    });

    const clientData = response.data.data || [];
    const totalHits = response.data.total || 0;
    const clientIds = clientData.map((d: any) => d.id);

    let finalClientIds: string[] = [];

    if (user.role === UserRolesEnum.CASE_MANAGER && user.caseManagerType !== caseManagerTypes.PINCC_PARTNER) {
      const referralSource = getReferralSourceByCaseManagerType(user?.caseManagerType || '');
      const cmQuery = query(collection(db, 'clients'), where('referralSource', '==', referralSource));
      const cmSnapshot = await getDocs(cmQuery);
      const referredIds = cmSnapshot.docs.map((doc) => doc.id);
      finalClientIds = clientIds.filter((id: any) => referredIds.includes(id));
    } else if (user.role === UserRolesEnum.TRAINER) {
      const ptcmData = await getClientMapDataByMultipleClientId(clientIds);
      finalClientIds = ptcmData.filter((d: any) => d.trainerId === user.id).map((d: any) => d.clientId);
    } else {
      finalClientIds = clientIds;
    }

    const clientList = await getMultipleClientListDataByIds(finalClientIds);

    return { totalHits, clients: clientList };
  } catch (err) {
    toast.error('Error Searching Clients!');
    return [];
  }
};

export const getMultipleClientByIds = async (clientIds: string[]) => {
  try {
    if (!clientIds || clientIds.length === 0) return [];

    const BATCH_SIZE = 10;
    const clientList: any[] = [];

    for (let i = 0; i < clientIds.length; i += BATCH_SIZE) {
      const batchIds = clientIds.slice(i, i + BATCH_SIZE);
      const q = query(collection(db, 'clients'), where('__name__', 'in', batchIds));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (!data.account || data.account !== 'deleted') {
          clientList.push({
            id: docSnap.id,
            ...data,
          });
        }
      });
    }

    return clientList;
  } catch (err: any) {
    toast.error('Error Retrieving Clients!');
    return [];
  }
};

export const getMultipleClientListDataByIds = async (clientIds: string[]) => {
  try {
    if (clientIds.length === 0) return [];

    if (clientIds.length > 10) {
      throw new Error('getMultipleClientListDataByIds: cannot fetch more than 10 IDs using "in" operator');
    }

    const q = query(collection(db, 'clientList'), where('__name__', 'in', clientIds));
    const snap = await getDocs(q);

    const results: any[] = [];

    snap.forEach((doc) => {
      const data = doc.data();
      if (data.account !== ClientAccount.DELETED) {
        results.push({ id: doc.id, ...data });
      }
    });

    // Sort by createdAt (newest first)
    results.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? 0;
      return bTime - aTime;
    });

    return results;
  } catch (err) {
    toast.error('Error Retrieving Clients!');
    return [];
  }
};

export const searchClients = async (
  page: number,
  searchQuery: string,
  searchQueryType: string,
  filterStatus: string,
  user: UserModel
) => {
  try {
    const searchResult = await searchClientsByQuery(searchQuery, searchQueryType, user, page, 10, filterStatus);
    if (Array.isArray(searchResult)) {
      throw new Error('Unexpected response format from searchClientsByQuery');
    }
    const { totalHits, clients } = searchResult;
    const filteredClients =
      filterStatus !== 'all' ? clients.filter((c) => c.clientStatus === filterStatus) : [...clients];

    const clientList = filteredClients.map((item) => ({
      ...item,
      programTrackName: getProgramTrackName(item?.programTrackData || []),
    }));

    const totalPages = Math.ceil(totalHits / 10);

    return {
      totalRecords: totalHits,
      currentPage: page,
      clients: clientList,
      totalPages,
    };
  } catch (error) {
    toast.error('Error Searching Clients!');
    return {
      totalRecords: 0,
      currentPage: 1,
      clients: [],
      totalPages: 0,
    };
  }
};

export const uploadFiles = async (
  files: any,
  options?: { clientUID?: string; workshopId?: string; temp?: boolean }
): Promise<{ urls: string[]; storagePaths: string[] }> => {
  try {
    if (!files) throw new Error();
    const urls: string[] = [];
    const storagePaths: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uuid = uuidv7();
      let path: string;
      if (options?.temp) path = `temp/${uuid}/${file.name}`;
      else if (options?.clientUID) path = `files/clients/${options.clientUID}/${uuid}/${file.name}`;
      else if (options?.workshopId) path = `files/workshops/${options.workshopId}/${uuid}/${file.name}`;
      else path = `files/${uuid}/${file.name}`;

      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      urls.push('');
      storagePaths.push(path);
    }
    return { urls, storagePaths };
  } catch (error: any) {
    toast.error('Error Uploading Files!');
    throw error;
  }
};

export const getFileUrl = async (storagePath: string, clientId?: string): Promise<string> => {
  const result: any = await httpsCallable(functions, 'getFileUrlV2')({ storagePath, clientId });
  return result.data.url;
};

const queryFor154ELetter = async (clientId: string) => {
  const collectionRef = collection(db, 'programTrackClientMap');
  const q = query(
    collectionRef,
    where('clientId', '==', clientId),
    where('status', 'in', [ClientMapStatus.ACTIVE, ClientMapStatus.REVIEW, ClientMapStatus.PRE_COMPLETE])
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot;
};

export const upload154ELetter = async (letter: any, clientId: any) => {
  try {
    if (!letter || !clientId) {
      throw new Error();
    }

    const querySnapshot = await queryFor154ELetter(clientId);

    if (!querySnapshot.empty) {
      const ptcmDoc = querySnapshot.docs[0].data();
      if (ptcmDoc.status === ClientMapStatus.ACTIVE || ptcmDoc.status === ClientMapStatus.REVIEW) {
        const uuid = uuidv7();
        const storagePath = `letters_145E/${clientId}/${uuid}/${letter.name}`;
        const storageRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(storageRef, letter);

        if (snapshot) {
          const payload = { id: querySnapshot.docs[0].id, url: '', name: letter.name, storagePath };
          const response: any = await httpsCallable(functions, 'set154ELetterUrlV2')(payload);

          if (response.data.success) {
            toast.success('File Uploaded Successfully!');
            return {
              url: response.data.data.letter_154E.url,
              storagePath: response.data.data.letter_154E.storagePath,
            };
          }
        } else {
          throw new Error();
        }
      } else if (ptcmDoc.status === ClientMapStatus.PRE_COMPLETE) {
        toast.error('School Letter Upload Not Allowed In Pre-Complete Stage!');
        return;
      }
    } else {
      throw new Error();
    }
  } catch (error) {
    toast.error('Error While Uploading!');
  }
};

export const approve154ELetter = async (clientId: any) => {
  try {
    if (!clientId) {
      throw new Error();
    }

    const querySnapshot = await queryFor154ELetter(clientId);

    if (!querySnapshot.empty) {
      const response: any = await httpsCallable(functions, 'approve154ELetterV2')({ id: querySnapshot.docs[0].id });
      if (response.data.success) {
        toast.success('School Letter Approved');
      }
    } else {
      throw new Error('Document not found!');
    }
  } catch (error: any) {
    toast.error('Error Approving School Letter!');
  }
};

export const getLiveClientInfoById = async (clientId: any, callback: Function) => {
  try {
    if (!clientId) {
      throw new Error();
    }

    const clientDocRef = doc(db, 'clients', clientId);

    const unsubscribe = onSnapshot(clientDocRef, async (snapshot) => {
      const letter154EDoc = await queryFor154ELetter(clientId);
      let letter154EData = {};

      if (!letter154EDoc.empty) {
        letter154EData = letter154EDoc.docs[0].data();
      }

      if (snapshot.exists()) {
        const clientData = { id: snapshot.id, ...letter154EData, ...snapshot.data() };
        callback(clientData);
      } else {
        callback({});
      }
    });

    return unsubscribe;
  } catch (error: any) {
    toast.error('Error Retrieving Client Data!');
    return () => {}; // Return a dummy function if an error occurs
  }
};

export const getClientInfoById = async (clientId: any) => {
  try {
    if (!clientId) {
      throw new Error();
    }

    const clientDocRef = doc(db, 'clients', clientId);
    const clientDoc = await getDoc(clientDocRef);
    const letter154EDoc = await queryFor154ELetter(clientId);
    let letter154EData = {};
    if (!letter154EDoc.empty) {
      letter154EData = letter154EDoc.docs[0].data();
    }
    if (clientDoc.exists()) {
      return { id: clientDoc.id, ...letter154EData, ...clientDoc.data() };
    } else {
      return {};
    }
  } catch (error: any) {
    console.log(error);
    toast.error('Error Retrieving Client Data!');
  }
};

export const addNewProspectiveClient = async (email: string, infoSessionId: string, userRole?: string) => {
  const hasPermission = shouldShowButton('inviteProspectiveClient', userRole);
  if (!hasPermission) {
    throw new Error('You do not have permission to invite Prospective Client');
  }
  const clientReportedIpv4 = await fetchClientReportedIpv4();
  const response = await httpsCallable(
    functions,
    'inviteProspectiveClientV2'
  )({ email, infoSessionId, clientReportedIpv4 });
  return response.data;
};

export const getClientListByNavigatorId = async (navigatorId: string, page: number) => {
  let result: ICLientMetaInfo = {
    totalRecords: 0,
    currentPage: page,
    clients: [],
    totalPages: 0,
  };

  try {
    const clientRef = collection(db, 'clients');
    const q = query(clientRef, where('assignedTo', '==', navigatorId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const clientList: any = [];
      querySnapshot.forEach((doc) => {
        if (doc.data().account !== ClientAccount.DELETED) {
          clientList.push({ id: doc.id, ...doc.data() });
        }
      });
      const pageSize = 10;
      const offset = (page - 1) * pageSize;
      const totalPages = Math.ceil(clientList.length / pageSize);
      const limitedClients = clientList.slice(offset, offset + pageSize);
      result = {
        totalRecords: clientList.length,
        currentPage: page,
        clients: limitedClients,
        totalPages: totalPages,
      };
    }
    return result;
  } catch (err: any) {
    //catch errors
  }
};

export const assignNavigatorToClient = async (clientId: string, navigatorId: string) => {
  try {
    if (!clientId || !navigatorId) {
      throw new Error();
    }

    const payload = {
      id: clientId,
      navigatorId: navigatorId,
    };
    const response: any = await httpsCallable(functions, 'setNavigatorToClientV2')(payload);

    if (response.data.success) {
      toast.success('Navigator Assigned');
    }
  } catch (error: any) {
    toast.error('Error Assigning Navigator To Client!');
  }
};

export const suspendClient = async (clientId: string, suspensionReason: string, suspensionReasonId: number) => {
  try {
    if (!clientId || !suspensionReason || !suspensionReasonId) {
      throw new Error();
    }

    const payload = {
      id: clientId,
      suspensionReason: suspensionReason,
      suspensionReasonId: suspensionReasonId,
    };

    const response: any = await httpsCallable(functions, 'setClientSuspendedV2')(payload);

    if (response.data.success) {
      toast.success('Client Suspended!');
    }
  } catch (error: any) {
    toast.error('Error: Unable To Suspend Client!');
  }
};
export const enrollClient = async (clientId: string, programTrackId: string) => {
  try {
    if (!clientId) {
      throw new Error('clientId is null or undefined');
    }
    const payload = {
      id: clientId,
      programTrackId: programTrackId,
    };
    const response: any = await httpsCallable(functions, 'setClientEnrolledV2')(payload);

    if (response.data.success) {
      toast.success('Client Enrolled!');
    }
  } catch (error: any) {
    toast.error('Error: Unable To Enroll Client!');
  }
};
export const cancelClientEnrollment = async (clientId: string) => {
  try {
    if (!clientId) {
      throw new Error();
    }

    const response: any = await httpsCallable(functions, 'cancelClientEnrollmentV2')({ id: clientId });

    if (response.data.success) {
      toast.success('Enrollment Cancelled Successfully!');
    }
  } catch (error: any) {
    toast.error('Error: Unable To Cancel Client Enrollment!');
  }
};
export const cancelClientIndividualEnrollment = async (payload: ICancelIndividualEnrollment) => {
  try {
    const response: any = await httpsCallable(functions, 'setClientIndvidualEnrollmentCancelV2')(payload);

    if (response.data.success) {
      toast.success('Enrollment Cancelled Successfully!');
    }
  } catch (error: any) {
    toast.error('Error: Unable To Cancel Client Enrollment!');
  }
};

export const getUserInfoByUid = async (uid: string, isStaff: boolean) => {
  const clientsQuery = doc(db, 'clients', uid);
  const staffQuery = doc(db, 'staff', uid);

  if (!isStaff) {
    const docSnap = await getDoc(clientsQuery);
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as unknown as Promise<ICommonClient>;
  } else {
    const docSnap = await getDoc(staffQuery);
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as unknown as Promise<ICommonClient>;
  }
};

// filter clients on basis of db fields
export const getFilteredClientList = async (fieldName: string, fieldValue: any) => {
  try {
    if (!fieldName || !fieldValue) {
      throw new Error();
    }

    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where(fieldName, '==', fieldValue));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const filteredClientList: any = [];
      querySnapshot.forEach((doc) => {
        filteredClientList.push({ id: doc.id, ...doc.data() });
      });
      return filteredClientList;
    } else {
      return [];
    }
  } catch (err: any) {
    toast.error('Error Retrieving Client Data!');
  }
};

export const getPendingEnrollmentClients = async () => {
  try {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('status', '==', ClientStatus.PENDING_ENROLLMENT));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const clientList: any = [];
      querySnapshot.forEach((doc) => {
        clientList.push({ id: doc.id, ...doc.data() });
      });
      return clientList;
    } else {
      return [];
    }
  } catch (err: any) {
    toast.error('Error Retrieving Client Data!');
  }
};

export const getClientListByUserId = async (userId: string) => {
  try {
    if (!userId) {
      throw new Error();
    }
    const clientRef = collection(db, 'clients');
    const q = query(clientRef, where('createdBy', '==', userId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const clientList: any = [];
      querySnapshot.forEach((doc) => {
        clientList.push({ id: doc.id, ...doc.data() });
      });
      return clientList;
    }
    return [];
  } catch (err: any) {
    console.log('Error Retrieving Client Data!');
  }
};

export const addDocument = async (
  clientId: string,
  payload: IDocumentData,
  userRole: string,
  isClientView: boolean | undefined
) => {
  try {
    if (!clientId) {
      throw new Error();
    }
    if (
      !isClientView &&
      ![
        UserRolesEnum.ADMIN,
        UserRolesEnum.SUPER_ADMIN,
        UserRolesEnum.NAVIGATOR,
        UserRolesEnum.EMPLOYMENT_LIAISON,
      ].includes(userRole)
    ) {
      throw new Error(`You do not have permission to upload client documents`);
    }

    const clientRef = doc(db, 'clients', clientId);
    const clientSnapshot = await getDoc(clientRef);

    if (!clientSnapshot.exists()) {
      throw new Error(`Client does not exist.`);
    }
    const clientData = clientSnapshot.data();

    const { urls, storagePaths } = await uploadFiles([payload.file], { clientUID: clientId });

    const newDocument = {
      id: clientId,
      name: `${clientData.lastName}${clientData.firstName}_${payload.name}`,
      url: urls[0],
      storagePath: storagePaths[0],
      uploadedBy: payload.uploadedBy,
      role: payload.role,
      documentId: uuidv4(),
      isClientView: isClientView || false,
    };
    const response: any = await httpsCallable(functions, 'addDocumentV2')(newDocument);
    if (response.data.success) {
      toast.success('Document Uploaded Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Uploading Document!');
  }
};

export const getClientDocuments = async (clientId: string) => {
  try {
    if (!clientId) {
      throw new Error();
    }
    const clientRef = doc(db, 'clients', clientId);
    const clientSnapshot = await getDoc(clientRef);

    if (clientSnapshot.exists()) {
      const currentDocuments = clientSnapshot.data()?.documents || [];
      return currentDocuments;
    } else {
      return [];
    }
  } catch (err: any) {
    toast.error('Error Fetching Documents!');
  }
};

export const deleteDocument = async (clientId: string, documentId: string, userRole: string) => {
  try {
    if (!clientId || !documentId) {
      throw new Error();
    }
    if (
      userRole !== UserRolesEnum.ADMIN &&
      userRole !== UserRolesEnum.NAVIGATOR &&
      userRole !== UserRolesEnum.EMPLOYMENT_LIAISON
    ) {
      throw new Error(`You do not have permission to delete clients documents`);
    }

    const payload = {
      id: clientId,
      documentId: documentId,
    };

    const response: any = await httpsCallable(functions, 'deleteDocumentV2')(payload);
    if (response.data.success) {
      toast.success('Document Deleted Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Deleting Document!');
  }
};

export const addNote = async (payload: INote) => {
  try {
    if (!payload || !payload.clientId) {
      throw new Error();
    }
    const newNote: any = {
      id: payload.clientId,
      title: payload.title,
      description: payload.description,
      createdBy: payload.createdBy,
      role: payload.role,
      noteId: uuidv4(),
    };
    if (payload.category) {
      newNote.category = payload.category;
    }
    const response: any = await httpsCallable(functions, 'createNoteV2')(newNote);
    if (response.data.success) {
      toast.success('New Note Added Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Adding Note!');
  }
};

export const editNote = async (payload: IEditNote) => {
  try {
    if (!payload.clientId || !payload.noteId) {
      throw new Error();
    }
    const editNote: any = {
      id: payload.clientId,
      title: payload.title,
      description: payload.description,
      noteId: payload.noteId,
    };
    if (payload.category) {
      editNote.category = payload.category;
    }
    const response: any = await httpsCallable(functions, 'editNoteV2')(editNote);
    if (response.data.success) {
      toast.success('Note Updated Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Updating Note!');
  }
};

export const deleteNote = async (clientId: string, noteId: string) => {
  try {
    if (!clientId || !noteId) {
      throw new Error();
    }
    const payload = {
      id: clientId,
      noteId: noteId,
    };
    const response: any = await httpsCallable(functions, 'deleteNoteV2')(payload);
    if (response.data.success) {
      toast.success('Note Deleted Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Deleting Note!');
  }
};

export const getAllNotes = async (clientId: string, callback: Function) => {
  try {
    if (!clientId) {
      throw new Error();
    }
    const clientDocRef = doc(db, 'clients', clientId);
    const unsubscribe = onSnapshot(clientDocRef, async (querySnapshot) => {
      if (querySnapshot.exists()) {
        const notes = querySnapshot.data().notes;
        if (notes) {
          const creatorIds: string[] = notes.map((note: INote) => note.createdBy);
          const creatorInfoList = await getMultipleUserInfoByUserIds(creatorIds);

          for (const note of notes) {
            const creatorInfo: any = creatorInfoList.find((item: any) => item.id === note.createdBy);
            if (creatorInfo) {
              note.creatorFullName = creatorInfo.firstName + ' ' + creatorInfo.lastName;
            }
          }

          // Sort by createdAt (latest first)
          notes.sort((a: INote, b: INote) => {
            if (a.createdAt && b.createdAt) {
              return b.createdAt.toMillis() - a.createdAt.toMillis();
            }
          });

          callback(notes);
        }
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    toast.error('Error Fetching Notes!');
  }
};

export const updateClientInterest = async (clientId: string, interestedIn: string) => {
  try {
    if (!clientId) {
      throw new Error();
    }

    const response: any = await httpsCallable(
      functions,
      'setClientInterestV2'
    )({ id: clientId, interestedIn: interestedIn });

    if (response.data.success) {
      toast.success('Interest Saved!');
    }
  } catch (error: any) {
    toast.error('Error Updating Client Ineterst!');
  }
};

export const isNavigatorAssignedToClient = async (navigatorId: string) => {
  try {
    if (!navigatorId) {
      throw new Error();
    }

    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('assignedTo', '==', navigatorId));
    const querySnapshot = await getDocs(q);

    return !querySnapshot.empty ? true : false;
  } catch (err: any) {
    toast.error('Error Retrieving Data!');
  }
};

export const isEmpLiaisonAssignedToClient = async (liaisonId: string) => {
  try {
    if (!liaisonId) {
      throw new Error();
    }

    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('employmentLiaisonId', '==', liaisonId));
    const querySnapshot = await getDocs(q);

    return !querySnapshot.empty ? true : false;
  } catch (err: any) {
    toast.error('Error Retrieving Data!');
  }
};

export const deleteClient = async (id: string) => {
  try {
    const response = await httpsCallable(functions, 'deleteClientV2')({ id: id });
    return response.data;
  } catch (error: any) {
    console.log('Error Deleting Client!');
  }
};

export const unenrollClient = async (id: string) => {
  try {
    const response = await httpsCallable(functions, 'unenrollClientV2')({ id: id });
    return response.data;
  } catch (error: any) {
    toast.error('Error Unenrolling Client!');
  }
};

export const getClientActivityLogsById = async (clientId: string, callback: Function) => {
  try {
    if (!clientId) {
      throw new Error('clientId is either null or undefined!');
    }
    const clientDocRef = doc(db, 'activityLogs', clientId);
    const unsubscribe = onSnapshot(clientDocRef, async (querySnapshot) => {
      const activityLogs: any = [];
      if (querySnapshot.exists()) {
        querySnapshot.data().activityLogs.forEach((item: any) => {
          activityLogs.push({
            timestamp: convertTimeStampToDate(item.timestamp).tz('America/New_York').format('YYYY-MM-DD hh:mm A'),
            context: item.text,
            realTime: item?.realTime || '',
            logId: item?.logId || '',
          });
        });
      }
      callback(activityLogs);
    });
    return unsubscribe;
  } catch (err: any) {
    toast.error('Error Retrieving Data!');
  }
  return [];
};

export const getAllClients = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'clients'));
    if (!querySnapshot.empty) {
      const clientList: any = [];
      querySnapshot.forEach((doc) => {
        if (doc.data().account !== ClientAccount.DELETED) {
          clientList.push({ id: doc.id, ...doc.data() });
        }
      });
      return clientList;
    } else {
      return [];
    }
  } catch (error: any) {
    toast.error('Error Retrieving All Clients!');
  }
};

export const getClientsLive = (status: string, callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const qry = query(collectionRef, where('status', '==', status));
    const unsubscribe = onSnapshot(qry, (querySnapshot) => {
      const list: any[] = [];
      for (const doc of querySnapshot.docs) {
        const docData = doc.data();
        if (docData?.account !== 'deleted') {
          const updatedDocData = { id: doc.id, firstName: docData.firstName, lastName: docData.lastName };
          list.push(updatedDocData);
        }
      }
      callback(list);
    });

    return unsubscribe;
  } catch (err: any) {
    return () => {};
  }
};

export const getClientsPaginated = async (
  status: string,
  limitCount: number,
  startAfterDoc: any = null
): Promise<{ clients: ICommonClient[]; lastDoc: any }> => {
  const collectionRef = collection(db, 'clients');
  let qry = query(collectionRef, where('status', '==', status), orderBy('firstName'), limit(limitCount));

  if (startAfterDoc) {
    qry = query(
      collectionRef,
      where('status', '==', status),
      orderBy('firstName'),
      startAfter(startAfterDoc),
      limit(limitCount)
    );
  }

  const querySnapshot = await getDocs(qry);
  const list: any[] = [];

  querySnapshot.forEach((doc) => {
    const docData = doc.data();
    list.push({
      id: doc.id,
      firstName: docData.firstName,
      lastName: docData.lastName,
    });
  });

  const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
  return { clients: list, lastDoc: lastVisible };
};

export const addNoteOnInterestUpdate = async (payload: INote) => {
  try {
    if (!payload || !payload.clientId) {
      throw new Error();
    }
    const newNote = {
      id: payload.clientId,
      title: payload.title,
      description: payload.description,
      createdBy: payload.createdBy,
      role: payload.role,
      noteId: uuidv4(),
    };
    await httpsCallable(functions, 'createNoteV2')(newNote);
  } catch (err: any) {
    // err
  }
};

export const getClientsWithPendingSchoolLetter = async (trainerId: string) => {
  const collectionRef = collection(db, 'programTrackClientMap');
  const q = query(collectionRef, where('letter_154E', '==', null), where('trainerId', '==', trainerId));
  const querySnapshot = await getDocs(q);

  const clientIds: any = [];
  if (!querySnapshot.empty) {
    querySnapshot.forEach((doc) => {
      clientIds.push(doc.data().clientId);
    });

    const clientList = getMultipleClientByIds(clientIds);

    return clientList;
  } else {
    return [];
  }
};

export const registerProspectiveClient = async (payload: any) => {
  try {
    const clientReportedIpv4 = await fetchClientReportedIpv4();
    const response = await httpsCallable(functions, 'registerProspectiveClientV2')({ ...payload, clientReportedIpv4 });
    return response.data;
  } catch (error: any) {
    console.log('Error While Registering a Client!');
  }
};

export const assignEmploymentLiaison = async (clientId: string, employmentLiaisonId: string) => {
  try {
    const payload = { id: clientId, employmentLiaisonId: employmentLiaisonId };
    const response: any = await httpsCallable(functions, 'setEmpLiaisonToClientV2')(payload);
    if (response.data.success) {
      return true;
    } else {
      throw new Error();
    }
  } catch (error: any) {
    toast.error('Error While Assigning Employment Liaison To Client!');
  }
};

export const setWorkshopToClient = async (clientId: string, workshopId: string) => {
  try {
    const payload = { id: clientId, workshopId: workshopId };
    const response: any = await httpsCallable(functions, 'enrollClientInWorkshopV2')(payload);
    if (response.data.success) {
      return true;
    } else {
      throw new Error();
    }
  } catch (error: any) {
    toast.error('Error While Assigning Workshop To Client!');
  }
};

export const fetchClientCompletedWorkshop = async (callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const unsubscribe = onSnapshot(collectionRef, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientIds: string[] = [];
        querySnapshot.docs.forEach((doc: any) => {
          if (
            doc.data().account !== userAccountStatus.DELETED &&
            doc.data().workshopAttendance &&
            doc.data().completedWorkshops
          ) {
            clientIds.push(doc.id);
          }
        });
        const clientList = await getMultipleClientByIds(clientIds);
        callback(clientList);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    throw new Error('Error While Fetching Client Completed Workshop');
  }
};

export const fetchPCForAttendanceRoster = async (infoSessionId: string, callback: Function) => {
  try {
    const collectionRef = collection(db, 'clients');
    const q = query(collectionRef, where('status', '==', ClientStatus.PROSPECTIVE), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientList: string[] = [];
        querySnapshot.docs.forEach((doc: any) => {
          if (
            doc.data().account !== userAccountStatus.DELETED &&
            doc.data().infoSessionIds &&
            !doc.data().infoSessionIds.includes(infoSessionId)
          ) {
            clientList.push({ id: doc.id, ...doc.data() });
          }
        });
        callback(clientList);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    throw new Error('Error While Fetching Prospective Clients');
  }
};

export const markClientsWorkshopCompleted = async (payload: IClientJRTComplete) => {
  try {
    const response: any = await httpsCallable(functions, 'markClientsWorkshopCompletedV2')(payload);
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Marking Job Readiness Training Completed!');
    }
  } catch (error) {
    toast.error('Error While Marking Job Readiness Training Completed!');
  }
};

export const markJobInterviewAttendance = async (id: string, jobIds: string[]) => {
  try {
    const response: any = await httpsCallable(
      functions,
      'markClientsJobInterviewAttendanceV2'
    )({ id: id, jobIds: jobIds });
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Marking Job Interview Attendance!');
    }
  } catch (error) {
    toast.error('Error While Marking Job Interview Attendance!');
  }
};

export const markClientReadyForEmployment = async (clientId: string) => {
  try {
    const response: any = await httpsCallable(functions, 'markClientReadyForEmploymentV2')({ id: clientId });
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Marking Client Ready for Employment!');
    }
  } catch (error) {
    toast.error('Error While Marking Client Ready for Employment!');
  }
};

export const markEmploymentIntakeComplete = async (clientId: string, intakeStatus: string) => {
  try {
    const response: any = await httpsCallable(
      functions,
      'markClientEmploymentIntakeCompleteV2'
    )({ id: clientId, intakeStatus: intakeStatus });
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Marking Client Employment Intake Complete!');
    }
  } catch (error) {
    toast.error('Error While Marking Client Employment Intake Complete!');
  }
};

export const convertClientToProspective = async (clientId: string) => {
  try {
    const response: any = await httpsCallable(functions, 'convertClientToProspectiveV2')({ id: clientId });
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Converting Client to Prospective!');
    }
  } catch (error) {
    toast.error('Error While Converting Client to Prospective!');
  }
};

export const getClientListByEmpLiaisonId = async (employmentLiaisonId: string, page: number, callback: Function) => {
  let result: ICLientMetaInfo = {
    totalRecords: 0,
    currentPage: page,
    clients: [],
    totalPages: 0,
  };
  try {
    const clientRef = collection(db, 'clients');
    const unsubscribe = onSnapshot(clientRef, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientList: any = [];
        querySnapshot.forEach((doc) => {
          if (
            doc.data().account !== ClientAccount.DELETED &&
            doc.data().employmentLiaisonId &&
            doc.data().employmentLiaisonId === employmentLiaisonId
          ) {
            clientList.push({ id: doc.id, ...doc.data() });
          }
        });
        const pageSize = 10;
        const offset = (page - 1) * pageSize;
        const totalPages = Math.ceil(clientList.length / pageSize);
        const limitedClients = clientList.slice(offset, offset + pageSize);
        result = {
          totalRecords: clientList.length,
          currentPage: page,
          clients: limitedClients,
          totalPages: totalPages,
        };
        callback(result);
      } else {
        callback(result);
      }
    });
    return unsubscribe;
  } catch (err: any) {
    //catch errors
  }
};

export const getClientsEmploymentPageData = async (
  page: number,
  user: UserModel,
  activeIds: number[],
  sortModel: ISortModel[],
  callback: Function
) => {
  let result: ICLientMetaInfo = {
    totalRecords: 0,
    currentPage: page,
    clients: [],
    totalPages: 0,
  };

  try {
    const clientRef = collection(db, 'clients');
    const unsubscribe = onSnapshot(clientRef, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        const clientList: any = [];
        for (const doc of querySnapshot.docs) {
          const data = doc.data();

          if (data.account === ClientAccount.DELETED) continue;

          const id = doc.id;
          let empStatus = '';

          // Step 1: Determine empStatus
          if (data.status === ClientStatus.PROSPECTIVE) {
            empStatus = ClientStatus.PROSPECTIVE;
          } else if (!data.employmentLiaisonId && !data.employmentReadiness) {
            empStatus = 'Pending Employment Readiness';
          } else if (!data.employmentLiaisonId && data.employmentReadiness) {
            empStatus = 'Pending Employment Liaison';
          } else if (
            data.employmentLiaisonId &&
            !data.employmentIntakeSubmitted &&
            !data.workshopId &&
            !data.employment &&
            !data.scheduledJobInterview
          ) {
            empStatus = 'Pending Employment Intake';
          } else if (
            data.employmentLiaisonId &&
            data.workshopId &&
            !data.completedWorkshops &&
            !data.scheduledJobInterview
          ) {
            empStatus = 'Enrolled in Workshop';
          } else if (
            data.employmentLiaisonId &&
            data.workshopId &&
            data.completedWorkshops &&
            !data.scheduledJobInterview
          ) {
            empStatus = 'Job Ready';
          } else if (data.employmentLiaisonId && data.scheduledJobInterview && !data.employment) {
            empStatus = 'Interview scheduled';
          } else if (data.employmentLiaisonId && data.employment) {
            empStatus = 'Hired';
          }

          // Step 2: Determine if we should include based on activeIds
          const shouldInclude =
            activeIds.includes(1) || // All
            (empStatus === 'Pending Employment Liaison' && activeIds.includes(2)) ||
            (empStatus === 'Pending Employment Intake' && activeIds.includes(3)) ||
            (empStatus === 'Enrolled in Workshop' && activeIds.includes(4)) ||
            (empStatus === 'Job Ready' && activeIds.includes(5)) ||
            (empStatus === 'Interview scheduled' && activeIds.includes(6)) ||
            (empStatus === 'Hired' && activeIds.includes(7));

          if (shouldInclude || activeIds.length < 1) {
            clientList.push({ id, ...data, empStatus });
          }
        }

        const lisisonIds: string[] = [];
        clientList.forEach((doc: any) => {
          if (doc.employmentLiaisonId) {
            lisisonIds.push(doc.employmentLiaisonId);
          }
        });
        const lisisonsInfoList = await getMultipleUserInfoByUserIds(lisisonIds);
        for (const client of clientList) {
          const reqLiaisonInfo: any = lisisonsInfoList.find((item: any) => item.id === client.employmentLiaisonId);
          client.employmentLiaisonName = reqLiaisonInfo ? reqLiaisonInfo.firstName + ' ' + reqLiaisonInfo.lastName : '';
        }

        if (sortModel.length > 0) {
          const field = sortModel[0].field;
          const sort = sortModel[0].sort;

          if (field === 'firstName') {
            clientList.sort((a: any, b: any) => {
              if (sort === 'asc') {
                return a.firstName.localeCompare(b.firstName);
              } else if (sort === 'desc') {
                return b.firstName.localeCompare(a.firstName);
              }
            });
          } else if (field === 'employmentLiaison') {
            clientList.sort((a: any, b: any) => {
              if (!a.employmentLiaisonName && !b.employmentLiaisonName) {
                return 0;
              } else if (!a.employmentLiaisonName) {
                return 1;
              } else if (!b.employmentLiaisonName) {
                return -1;
              } else {
                if (sort === 'asc') {
                  return a.employmentLiaisonName.localeCompare(b.employmentLiaisonName);
                } else if (sort === 'desc') {
                  return b.employmentLiaisonName.localeCompare(a.employmentLiaisonName);
                }
              }
            });
          } else if (field === 'interviewCount') {
            clientList.sort((a: any, b: any) => {
              const interviewCountA = a.scheduledJobInterview ? a.scheduledJobInterview.length : 0;
              const interviewCountB = b.scheduledJobInterview ? b.scheduledJobInterview.length : 0;

              if (sort === 'asc') {
                return interviewCountA - interviewCountB;
              } else if (sort === 'desc') {
                return interviewCountB - interviewCountA;
              }
              return 0;
            });
          }
        } else {
          clientList.sort((a: any, b: any) => {
            const aTime = a.createdAt?.toMillis?.() ?? 0;
            const bTime = b.createdAt?.toMillis?.() ?? 0;
            return bTime - aTime;
          });
        }

        const pageSize = 10;
        const offset = (page - 1) * pageSize;
        const totalPages = Math.ceil(clientList.length / pageSize);
        const limitedClients = clientList.slice(offset, offset + pageSize);

        result = {
          totalRecords: clientList.length,
          currentPage: page,
          clients: limitedClients,
          totalPages: totalPages,
        };

        callback(result);
      } else {
        callback(result);
      }
    });

    return unsubscribe;
  } catch (err: any) {
    // handle errors
  }
};
const doesClientMatchFilters = (client: any, filters: { [key: string]: string[] }) => {
  return Object.entries(filters).every(([filterKey, filterValues]) => {
    if (!filterValues || filterValues.length === 0) return true;
    return filterValues.some((value) => {
      const clientValue = client[filterKey];
      if (Array.isArray(clientValue)) {
        return clientValue.includes(value);
      }
      return clientValue === value;
    });
  });
};

export const getClientsEmploymentDataByFilter = async (
  page: number,
  filterOptions: { [key: string]: string[] },
  sortModel: ISortModel[],
  callback: Function
) => {
  let result: ICLientMetaInfo = {
    totalRecords: 0,
    currentPage: page,
    clients: [],
    totalPages: 0,
  };

  try {
    const clientRef = collection(db, 'clients');
    const unsubscribe = onSnapshot(clientRef, async (querySnapshot) => {
      if (!querySnapshot.empty) {
        let clientList: any = [];
        for (const doc of querySnapshot.docs) {
          const data = doc.data();
          if (data.account !== ClientAccount.DELETED) {
            let empStatus = '';
            if (data.status === ClientStatus.PROSPECTIVE) {
              empStatus = ClientStatus.PROSPECTIVE;
            } else if (!data.employmentLiaisonId && !data.employmentReadiness) {
              empStatus = 'Pending Employment Readiness';
            } else if (!data.employmentLiaisonId && data.employmentReadiness) {
              empStatus = 'Pending Employment Liaison';
            } else if (
              data.employmentLiaisonId &&
              !data.employmentIntakeSubmitted &&
              !data.workshopId &&
              !data.employment &&
              !data.scheduledJobInterview
            ) {
              empStatus = 'Pending Employment Intake';
            } else if (
              data.employmentLiaisonId &&
              data.workshopId &&
              !data.completedWorkshops &&
              !data.scheduledJobInterview
            ) {
              empStatus = 'Enrolled in Workshop';
            } else if (
              data.employmentLiaisonId &&
              data.workshopId &&
              data.completedWorkshops &&
              !data.scheduledJobInterview
            ) {
              empStatus = 'Job Ready';
            } else if (data.employmentLiaisonId && data.scheduledJobInterview && !data.employment) {
              empStatus = 'Interview scheduled';
            } else if (data.employmentLiaisonId && data.employment) {
              empStatus = 'Hired';
            }
            if (filterOptions.employmentStatus && filterOptions.employmentStatus.length > 0) {
              for (const option of filterOptions.employmentStatus) {
                const shouldInclude =
                  (empStatus === 'Pending Employment Liaison' && option === '1') ||
                  (empStatus === 'Pending Employment Intake' && option === '2') ||
                  (empStatus === 'Enrolled in Workshop' && option === '3') ||
                  (empStatus === 'Job Ready' && option === '4') ||
                  (empStatus === 'Interview scheduled' && option === '5') ||
                  (empStatus === 'Hired' && option === '6');

                if (shouldInclude) {
                  clientList.push({ id: doc.id, ...data, empStatus });
                }
              }
            } else {
              clientList.push({ id: doc.id, ...data, empStatus });
            }
          }
        }
        const processedFilters: { [key: string]: string[] } = {};
        if (filterOptions.employmentModuleStatus) {
          processedFilters.employmentModuleStatus = mapIdsToValues(
            filterOptions.employmentModuleStatus,
            empStatusOptions
          );
        }

        if (filterOptions.employmentType) {
          processedFilters.employmentType = mapIdsToValues(filterOptions.employmentType, employmentTypes);
        }

        if (filterOptions.employmentModuleStatus || filterOptions.employmentType) {
          clientList = clientList.filter((client: any) => doesClientMatchFilters(client, processedFilters));
        }

        const lisisonIds: string[] = [];
        clientList.forEach((doc: any) => {
          if (doc.employmentLiaisonId) {
            lisisonIds.push(doc.employmentLiaisonId);
          }
        });
        const lisisonsInfoList = await getMultipleUserInfoByUserIds(lisisonIds);
        for (const client of clientList) {
          const reqLiaisonInfo: any = lisisonsInfoList.find((item: any) => item.id === client.employmentLiaisonId);
          client.employmentLiaisonName = reqLiaisonInfo ? reqLiaisonInfo.firstName + ' ' + reqLiaisonInfo.lastName : '';
        }

        if (sortModel.length > 0) {
          const field = sortModel[0].field;
          const sort = sortModel[0].sort;

          if (field === 'firstName') {
            clientList.sort((a: any, b: any) => {
              if (sort === 'asc') {
                return a.firstName.localeCompare(b.firstName);
              } else if (sort === 'desc') {
                return b.firstName.localeCompare(a.firstName);
              }
            });
          } else if (field === 'employmentLiaison') {
            clientList.sort((a: any, b: any) => {
              if (!a.employmentLiaisonName && !b.employmentLiaisonName) {
                return 0;
              } else if (!a.employmentLiaisonName) {
                return 1;
              } else if (!b.employmentLiaisonName) {
                return -1;
              } else {
                if (sort === 'asc') {
                  return a.employmentLiaisonName.localeCompare(b.employmentLiaisonName);
                } else if (sort === 'desc') {
                  return b.employmentLiaisonName.localeCompare(a.employmentLiaisonName);
                }
              }
            });
          } else if (field === 'interviewCount') {
            clientList.sort((a: any, b: any) => {
              const interviewCountA = a.scheduledJobInterview ? a.scheduledJobInterview.length : 0;
              const interviewCountB = b.scheduledJobInterview ? b.scheduledJobInterview.length : 0;

              if (sort === 'asc') {
                return interviewCountA - interviewCountB;
              } else if (sort === 'desc') {
                return interviewCountB - interviewCountA;
              }
              return 0; // Default case, should not happen
            });
          }
        }

        const pageSize = 10;
        const offset = (page - 1) * pageSize;
        const totalPages = Math.ceil(clientList.length / pageSize);
        const limitedClients = clientList.slice(offset, offset + pageSize);

        result = {
          totalRecords: clientList.length,
          currentPage: page,
          clients: limitedClients,
          totalPages: totalPages,
        };

        callback(result);
      } else {
        callback(result);
      }
    });

    return unsubscribe;
  } catch (err: any) {
    // handle errors
  }
};

export const searchClientsEmployment = async (searchQuery: string, searchQueryType: string, page: number) => {
  try {
    let result: ICLientMetaInfo = {
      totalRecords: 0,
      currentPage: page,
      clients: [],
      totalPages: 0,
    };

    if (searchQuery) {
      if (searchQuery.includes('@')) {
        searchQuery = searchQuery.split('@')[0];
      }
      const currentUser = getAuth().currentUser;
      console.log('currentUser in searchClientsEmployment', currentUser);
      const response: any = await httpsCallable(
        functions,
        'searchClientByNameV2'
      )({ query: searchQuery, type: searchQueryType });

      const clientData = response.data.data;
      const clientIds: any = [];
      let clientList = [];

      if (clientData.length > 0) {
        for (const data of clientData) {
          clientIds.push(data.id);
        }

        clientList = await getMultipleClientByIds(clientIds);

        // Fetch employmentLiaisonName for each client concurrently
        const getUserFullNamePromises = clientList.map(async (client: any) => {
          if (client.employmentLiaisonId) {
            client.employmentLiaisonName = await getUserFullNameById(client.employmentLiaisonId);
          }
        });

        await Promise.all(getUserFullNamePromises);

        const pageSize = 10;
        const offset = (page - 1) * pageSize;
        const totalPages = Math.ceil(clientList.length / pageSize);
        const limitedClients = clientList.slice(offset, offset + pageSize);

        result = {
          totalRecords: clientList.length,
          currentPage: page,
          clients: limitedClients,
          totalPages: totalPages,
        };
      } else {
        return result;
      }
    }

    return result;
  } catch (error) {
    // handle errors
  }
};

export const sendTextMessage = async (clientIds: string[], text: string) => {
  try {
    const payload: ISendTextMessage = {
      ids: clientIds,
      text: text,
    };
    const response: any = await httpsCallable(functions, 'SendTextMessageToClientsV2')(payload);
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Text Message!');
    }
  } catch (error) {
    toast.error('Error While Text Message!');
  }
};

export const updateClientsEmplInfo = async (payload: IClientEmplInfo) => {
  try {
    const response: any = await httpsCallable(functions, 'updateClientsEmploymentInfoV2')(payload);
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Updating Clinets Employment Info!');
    }
  } catch (error) {
    toast.error('Error While Updating Clinets Employment Info!');
  }
};
export const addRealTimeInActivityLogs = async (logId: string, clientId: string, realTime: string) => {
  try {
    const response: any = await httpsCallable(functions, 'addRealTimeInActivityLogsV2')({ logId, clientId, realTime });
    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error('Error While Creating or Updating Real Time!');
    }
  } catch (error) {
    toast.error('Error While Creating or Updating Real Time!');
  }
};

export const fetchClientsForTextMessaging = async (user: UserModel) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'clients'));
    if (!querySnapshot.empty) {
      const clientList: any = [];
      querySnapshot.forEach((doc) => {
        clientList.push({ id: doc.id, ...doc.data() });
      });
      if (user.role === UserRolesEnum.NAVIGATOR) {
        return clientList.filter((client: any) => client.assignedTo === user?.id);
      } else if (user.role === UserRolesEnum.EMPLOYMENT_LIAISON) {
        return clientList.filter((client: any) => client.employmentLiaisonId === user?.id);
      } else {
        return clientList;
      }
    } else {
      return [];
    }
  } catch (err: any) {
    // console err
  }
  return [];
};

export const toggleClientStatus = async (id: string, activeStatus: string) => {
  try {
    await httpsCallable(functions, 'toggleClientActiveStatusV2')({ id, activeStatus });
  } catch (error) {
    toast.error('Error While Creating or Updating Real Time!');
  }
};

export const listClientByFiltersApplied = async (
  page: number,
  user: UserModel,
  filterOptions: { [key: string]: string[] },
  sortModel: ISortModel[]
) => {
  try {
    const clientSnapshot = await getDocs(collection(db, 'clients'));
    if (clientSnapshot.empty) {
      return [];
    }
    const allClients: any[] = [];
    clientSnapshot.docs.forEach((doc) => {
      allClients.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    let clientList = allClients.filter((obj: any) => obj?.account !== ClientAccount.DELETED);

    if (user.role === UserRolesEnum.CASE_MANAGER && user.caseManagerType !== caseManagerTypes.PINCC_PARTNER) {
      const referralSource = getReferralSourceByCaseManagerType(user?.caseManagerType || '');
      clientList = clientList.filter((client: any) => client?.referralSource === referralSource);
    }

    const processedFilters: { [key: string]: string[] } = {};

    if (filterOptions.status) {
      processedFilters.status = mapIdsToValues(filterOptions.status, statusOptions);
    }
    if (filterOptions.gender) {
      processedFilters.gender = mapIdsToValues(filterOptions.gender, genderOptions);
    }
    if (filterOptions.ethnic) {
      processedFilters.ethnic = mapIdsToValues(filterOptions.ethnic, ethnicityOptions);
    }
    if (filterOptions.highestDL) {
      processedFilters.highestDL = mapIdsToValues(filterOptions.highestDL, highestDLOptions);
    }
    if (filterOptions.educationalBackground) {
      processedFilters.educationalBackground = mapIdsToValues(filterOptions.educationalBackground, educationOptions);
    }
    if (filterOptions.referralSource) {
      processedFilters.referralSource = mapIdsToValues(filterOptions.referralSource, referralSourceOptions);
    }
    if (filterOptions.publicAssistance) {
      processedFilters.publicAssistance = mapIdsToValues(filterOptions.publicAssistance, publicAssistanceOptions);
    }
    if (filterOptions.activeStatus) {
      processedFilters.activeStatus = mapIdsToValues(filterOptions.activeStatus, activeStatusOptions);
    }
    if (filterOptions.employmentStatus) {
      processedFilters.employmentStatus = mapIdsToValues(filterOptions.employmentStatus, empStatusOptions);
    }
    if (filterOptions.employmentType) {
      processedFilters.employmentType = mapIdsToValues(filterOptions.employmentType, employmentTypes);
    }
    if (filterOptions.interestedIn) {
      processedFilters.interestedIn = mapIdsToValues(filterOptions.interestedIn, interestedInFilterOptions);
    }
    if (filterOptions.assignedTo) {
      processedFilters.assignedTo = filterOptions.assignedTo;
    }

    const doesClientMatchFilters = (client: any, filters: { [key: string]: string[] }) => {
      return Object.entries(filters).every(([filterKey, filterValues]) => {
        if (!filterValues || filterValues.length === 0) return true;

        return filterValues.some((value) => {
          const clientValue = client[filterKey];
          if (Array.isArray(clientValue)) {
            return clientValue.includes(value);
          }
          return clientValue === value;
        });
      });
    };

    let filteredClient = clientList.filter((client: any) => doesClientMatchFilters(client, processedFilters));

    if (filterOptions.programTrack && filterOptions.programTrack.length > 0) {
      const q = query(
        collection(db, 'programTrackClientMap'),
        where('programTrackId', 'in', filterOptions.programTrack)
      );
      const querySnapshot = await getDocs(q);
      const clientIds = querySnapshot.docs.map((doc) => {
        return doc.data().clientId;
      });
      filteredClient = filteredClient.filter((client: any) => clientIds.includes(client.id));
    }

    const navigatorInfoIds = new Set<string>();
    const clientMapDataIds = new Set<string>();

    filteredClient.forEach((client: any) => {
      if (client.assignedTo) navigatorInfoIds.add(client.assignedTo);
      if (client.id) clientMapDataIds.add(client.id);
    });
    // Convert Set to Array before spreading
    const [navigatorInfo, clientMapData] = await Promise.all([
      getMultipleUserInfoByUserIds(Array.from(navigatorInfoIds)),
      getClientMapDataByMultipleClientId(Array.from(clientMapDataIds)),
    ]);

    const clientMapDataById = clientMapData.reduce((acc: any, data: any) => {
      if (!acc[data.clientId] || acc[data.clientId].enrollInitiatedAt < data.enrollInitiatedAt) {
        acc[data.clientId] = data;
      }
      return acc;
    }, {});

    const programTrackInfoIds = new Set<string>();

    filteredClient.forEach((client: any) => {
      client.navigatorInfo = navigatorInfo.find((data: any) => data.id === client.assignedTo) || '';
      client.clientMapData = clientMapDataById[client.id] || '';
      if (client.clientMapData && client.clientMapData.programTrackId) {
        programTrackInfoIds.add(client.clientMapData.programTrackId);
      }
    });

    // Convert Set to Array before spreading
    const programTrackInfo = await getMultipleProgramTracksById(Array.from(programTrackInfoIds));

    const programTrackInfoById = programTrackInfo.reduce((acc: any, data: any) => {
      acc[data.id] = data;
      return acc;
    }, {});

    filteredClient.forEach((client: any) => {
      client.programTrackInfo = programTrackInfoById[client.clientMapData.programTrackId] || '';
    });

    if (sortModel.length > 0) {
      const field = sortModel[0].field;
      const sort = sortModel[0].sort;

      if (field === 'client') {
        filteredClient.sort((a: any, b: any) => {
          if (sort === 'asc') {
            return a.firstName.localeCompare(b.firstName);
          } else if (sort === 'desc') {
            return b.firstName.localeCompare(a.firstName);
          }
        });
      } else if (field === 'clientStatus') {
        filteredClient.sort((a: any, b: any) => {
          if (sort === 'asc') {
            return a.status.localeCompare(b.status);
          } else if (sort === 'desc') {
            return b.status.localeCompare(a.status);
          }
        });
      } else if (field === 'navigator') {
        filteredClient.sort((a: any, b: any) => {
          if (!a.navigatorInfo && !b.navigatorInfo) {
            return 0;
          } else if (!a.navigatorInfo) {
            return 1;
          } else if (!b.navigatorInfo) {
            return -1;
          } else {
            if (sort === 'asc') {
              return a.navigatorInfo.firstName.localeCompare(b.navigatorInfo.firstName);
            } else if (sort === 'desc') {
              return b.navigatorInfo.firstName.localeCompare(a.navigatorInfo.firstName);
            }
          }
        });
      } else if (field === 'programTrack') {
        filteredClient.sort((a: any, b: any) => {
          if (!a.programTrackInfo && !b.programTrackInfo) {
            return 0;
          } else if (!a.programTrackInfo) {
            return 1;
          } else if (!b.programTrackInfo) {
            return -1;
          } else {
            if (sort === 'asc') {
              return a.programTrackInfo.name.localeCompare(b.programTrackInfo.name);
            } else if (sort === 'desc') {
              return b.programTrackInfo.name.localeCompare(a.programTrackInfo.name);
            }
          }
        });
      } else if (field === 'interestedIn') {
        filteredClient.sort((a: any, b: any) => {
          // if (a.interestedIn.length === 0 && b.interestedIn.length === 0) {
          //   return 0;
          // }
          // else if (a.interestedIn.length < b.interestedIn.length) {
          //   return 1;
          // } else if (a.interestedIn.length > b.interestedIn.length) {
          //   return -1;
          // }
          // else {
          //   if (sort === 'asc') {
          //     return a.interestedIn.join(',').localeCompare(b.interestedIn.join(','));
          //   } else if (sort === 'desc') {
          //     return b.interestedIn.join(',').localeCompare(a.interestedIn.join(','));
          //   }
          // }
          const aVal = a.interestedIn;
          const bVal = b.interestedIn;

          // Prioritize arrays first
          if (Array.isArray(aVal) && !Array.isArray(bVal)) return -1;
          if (Array.isArray(bVal) && !Array.isArray(aVal)) return 1;

          // Handle empty arrays next
          if (Array.isArray(aVal) && Array.isArray(bVal)) {
            if (aVal.length === 0 && bVal.length === 0) return 0;
            if (aVal.length === 0) return 1;
            if (bVal.length === 0) return -1;

            // Sort non-empty arrays alphabetically (with ascending or descending order)
            const comparisonResult = aVal.join(',').localeCompare(bVal.join(','));

            return sort === 'asc' ? comparisonResult : -comparisonResult;
          }

          // Lastly, handle empty strings (lowest priority)
          if (aVal === '' && bVal !== '') return 1;
          if (bVal === '' && aVal !== '') return -1;
          return 0;
        });
      }
    } else {
      filteredClient.sort((a: any, b: any) => {
        const dateA = a.createdAt ? convertTimeStampToDate(a.createdAt).toDate() : null;
        const dateB = b.createdAt ? convertTimeStampToDate(b.createdAt).toDate() : null;

        if (dateA && dateB) {
          return dateB.getTime() - dateA.getTime();
        } else if (dateB) {
          return 1; // Place documents without 'createdAt' field after those with 'createdAt'
        } else if (dateA) {
          return -1; // Place documents without 'createdAt' field before those with 'createdAt'
        } else {
          return 0; // Both documents don't have 'createdAt' field, no preference in order
        }
      });
    }

    const pageSize = 10;
    const offset = (page - 1) * pageSize;

    const totalPages = Math.ceil(filteredClient.length / pageSize);
    const limitedClients = filteredClient.slice(offset, offset + pageSize);
    const result: ICLientMetaInfo = {
      totalRecords: filteredClient.length,
      currentPage: page,
      clients: limitedClients,
      totalPages: totalPages,
    };
    return result;
  } catch (err: any) {
    toast.error('Error Retrieving Client Data!');
  }
};

export const getClientsWithJobInterview = async (jobId: string) => {
  try {
    const clientsRef = collection(db, 'clients');

    const q = query(clientsRef, where('scheduledJobInterview', '>', []));

    const querySnapshot = await getDocs(q);

    const clients = querySnapshot.docs
      .map((doc) => {
        const data = doc.data();

        const hasJobInterview = data.scheduledJobInterview.some(
          (interview: { jobId: string; scheduledBy: string }) => interview.jobId === jobId
        );

        if (hasJobInterview) {
          return {
            id: doc.id,
            ...data,
            name: `${data.firstName} ${data.lastName}`,
          };
        }

        return null;
      })
      .filter((client) => client !== null);

    return clients;
  } catch (error) {
    console.error('Error fetching clients with job interview:', error);
    throw new Error('Failed to fetch clients with job interview');
  }
};

export const fetchClientsHiredInDateRange = async (startDate: Dayjs, endDate: Dayjs) => {
  try {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('employment', '!=', null));

    const querySnapshot = await getDocs(q);

    const clients = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        name: `${doc.data().firstName} ${doc.data().lastName}`,
      }))
      .filter((client: any) => {
        const employment = client.employment;
        if (employment && employment.startAt) {
          const startAtDate = dayjs(employment.startAt).tz('America/New_York', true);

          return startAtDate.isAfter(startDate) && startAtDate.isBefore(endDate);
        }
        return false;
      });

    return clients;
  } catch (error) {
    console.error('Error Fetching Client Hired:', error);
    throw new Error('Failed To Fetch Client Hired!');
  }
};

export const fetchClientsHiredInDateRangeWithJobType = async (startDate: Dayjs, endDate: Dayjs, jobType: string) => {
  try {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('employment', '!=', null));
    const querySnapshot = await getDocs(q);

    const clients = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        name: `${doc.data().firstName} ${doc.data().lastName}`,
      }))
      .filter((client: any) => {
        const employment = client.employment;
        if (employment && employment.startAt) {
          const startAtDate = dayjs(employment.startAt).tz('America/New_York', true);
          return startAtDate.isAfter(startDate) && startAtDate.isBefore(endDate);
        }
        return false;
      });

    const jobIds = Array.from(new Set(clients.map((client: any) => client?.employment?.jobId).filter(Boolean)));

    const jobDataMap: Record<string, any> = await getMultipleJobByIdsForReport(jobIds);

    const clientList = clients.filter((client: any) => {
      const jobId = client?.employment?.jobId;
      const jobData = jobDataMap.find((job: any) => job.id === jobId);
      return jobData?.type === jobType;
    });
    return clientList;
  } catch (error) {
    console.error('Error Fetching Client Hired with job type:', error);
    throw new Error('Failed To Fetch Client Hired With Job Type!');
  }
};

export const fetchClientsWithJobOfferAccepted = async (startDate: Dayjs, endDate: Dayjs) => {
  try {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('scheduledJobInterview', '!=', null));
    const querySnapshot = await getDocs(q);

    const clients = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        name: `${doc.data().firstName} ${doc.data().lastName}`,
      }))
      .filter((client: any) => {
        const scheduledJobInterview = client.scheduledJobInterview;

        // Check if any interview has offerStatus 'OFFER_ACCEPTED' and scheduledAt within the date range
        return scheduledJobInterview.some((interview: any) => {
          const hasAcceptedOffer = interview?.offerStatus === jobOfferStatusEnums.OFFER_ACCEPTED;
          const hasScheduledAt = interview?.scheduledAt;

          if (hasAcceptedOffer && hasScheduledAt) {
            const scheduledAtDate = convertTimeStampToDate(interview.scheduledAt).tz('America/New_York', true);
            return scheduledAtDate.isAfter(startDate) && scheduledAtDate.isBefore(endDate);
          }
          return false;
        });
      });
    return clients;
  } catch (error) {
    console.error('Error Fetching Clients with Job Offer Accepted:', error);
    throw new Error('Failed To Fetch Clients With Job Offer Accepted!');
  }
};

export const saveMonitoringEntry = async (payload: IMonitoringEntry): Promise<{ exists: boolean }> => {
  try {
    const response: any = await httpsCallable(functions, 'saveMonitoringEntryWhileSignupV2')(payload);
    if (response.data.success) {
      console.log(response.data.message);
    }
    return { exists: response.data?.exists ?? false };
  } catch (error: any) {
    console.log('Error While Creating Monitoring Entry for Signup!');
    // On error, permit advance; the server-side duplicate-email check inside
    // createNewClientV2 at final submit is the last line of defense.
    return { exists: false };
  }
};
